/**
 * LOINC — Tests laboratoire (Logical Observation Identifiers Names and Codes)
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TestTube, Search, Beaker } from "lucide-react";
import { useTerminologyRelease } from "@/lib/terminology-client";

interface LoincTest {
  loinc: string;
  name: string;
  shortName: string;
  class: string;
  specimen: string;
  unit: string;
  refRange: string;
  category: "chimie" | "hematologie" | "microbiologie" | "immunologie" | "genetique" | "coagulation";
}

const LOINC_TESTS: LoincTest[] = [
  { loinc: "57021-8", name: "Hémoglobine glyquée / Hémoglobine totale [Fraction massique] dans Sang", shortName: "HbA1c", class: "Chimie", specimen: "Sang veineux", unit: "%", refRange: "< 5.7", category: "chimie" },
  { loinc: "4548-4", name: "Hémoglobine A1c/Hémoglobine totale dans Sang", shortName: "HbA1c (legacy)", class: "Chimie", specimen: "Sang veineux", unit: "%", refRange: "< 5.7", category: "chimie" },
  { loinc: "2345-7", name: "Glucose [Masse/Volume] dans Sang", shortName: "Glucose", class: "Chimie", specimen: "Sang veineux (à jeun)", unit: "mg/dL", refRange: "70-100", category: "chimie" },
  { loinc: "2093-3", name: "Cholestérol total [Masse/Volume] dans Sérum", shortName: "Cholestérol total", class: "Chimie", specimen: "Sérum", unit: "mg/dL", refRange: "< 200", category: "chimie" },
  { loinc: "2085-9", name: "HDL-Cholestérol [Masse/Volume] dans Sérum", shortName: "HDL", class: "Chimie", specimen: "Sérum", unit: "mg/dL", refRange: "> 40 (H) / > 50 (F)", category: "chimie" },
  { loinc: "13457-7", name: "LDL-Cholestérol (calculé) [Masse/Volume] dans Sérum", shortName: "LDL", class: "Chimie", specimen: "Sérum", unit: "mg/dL", refRange: "< 100", category: "chimie" },
  { loinc: "2571-8", name: "Triglycérides [Masse/Volume] dans Sérum", shortName: "Triglycérides", class: "Chimie", specimen: "Sérum (à jeun)", unit: "mg/dL", refRange: "< 150", category: "chimie" },
  { loinc: "1742-6", name: "ASAT (AST) [Enzymatique] dans Sérum", shortName: "ASAT", class: "Chimie", specimen: "Sérum", unit: "U/L", refRange: "< 35", category: "chimie" },
  { loinc: "1744-0", name: "ALAT (ALT) [Enzymatique] dans Sérum", shortName: "ALAT", class: "Chimie", specimen: "Sérum", unit: "U/L", refRange: "< 35", category: "chimie" },
  { loinc: "1743-4", name: "ALAT (ALT) [Enzymatique] dans Plasma", shortName: "ALAT (plasma)", class: "Chimie", specimen: "Plasma hépariné", unit: "U/L", refRange: "< 35", category: "chimie" },
  { loinc: "2324-2", name: "Gamma-glutamyltransférase [Enzymatique] dans Sérum", shortName: "GGT", class: "Chimie", specimen: "Sérum", unit: "U/L", refRange: "< 38 (H) / < 25 (F)", category: "chimie" },
  { loinc: "718-7", name: "Hémoglobine [Masse/Volume] dans Sang", shortName: "Hémoglobine", class: "Hématologie", specimen: "Sang veineux (EDTA)", unit: "g/dL", refRange: "13-17 (H) / 12-15 (F)", category: "hematologie" },
  { loinc: "789-8", name: "Numération érythrocytaire [Numérique/Volume] dans Sang", shortName: "Globules rouges", class: "Hématologie", specimen: "Sang veineux (EDTA)", unit: "10^6/µL", refRange: "4.5-5.5 (H) / 4.0-5.0 (F)", category: "hematologie" },
  { loinc: "6690-2", name: "Numération leucocytaire [Numérique/Volume] dans Sang", shortName: "Globules blancs", class: "Hématologie", specimen: "Sang veineux (EDTA)", unit: "10^3/µL", refRange: "4-10", category: "hematologie" },
  { loinc: "777-3", name: "Plaquettes [Numérique/Volume] dans Sang", shortName: "Plaquettes", class: "Hématologie", specimen: "Sang veineux (EDTA)", unit: "10^3/µL", refRange: "150-400", category: "hematologie" },
  { loinc: "11580-8", name: "TSH [Unité/Volume] dans Sérum", shortName: "TSH", class: "Immunologie", specimen: "Sérum", unit: "µUI/mL", refRange: "0.4-4.0", category: "immunologie" },
  { loinc: "3016-3", name: "T4 libre [Masse/Volume] dans Sérum", shortName: "T4 libre", class: "Immunologie", specimen: "Sérum", unit: "ng/dL", refRange: "0.8-1.8", category: "immunologie" },
  { loinc: "3024-7", name: "T3 libre [Masse/Volume] dans Sérum", shortName: "T3 libre", class: "Immunologie", specimen: "Sérum", unit: "pg/mL", refRange: "2.3-4.2", category: "immunologie" },
  { loinc: "2160-0", name: "Créatinine [Masse/Volume] dans Sérum", shortName: "Créatinine", class: "Chimie", specimen: "Sérum", unit: "mg/dL", refRange: "0.7-1.3 (H) / 0.6-1.1 (F)", category: "chimie" },
  { loinc: "3094-0", name: "Urée [Masse/Volume] dans Sérum", shortName: "Urée", class: "Chimie", specimen: "Sérum", unit: "mg/dL", refRange: "15-45", category: "chimie" },
  { loinc: "2951-2", name: "Sodium [Masse/Volume] dans Sérum", shortName: "Sodium", class: "Chimie", specimen: "Sérum", unit: "mmol/L", refRange: "135-145", category: "chimie" },
  { loinc: "2823-3", name: "Potassium [Masse/Volume] dans Sérum", shortName: "Potassium", class: "Chimie", specimen: "Sérum", unit: "mmol/L", refRange: "3.5-5.0", category: "chimie" },
  { loinc: "1751-7", name: "Albumine [Masse/Volume] dans Sérum", shortName: "Albumine", class: "Chimie", specimen: "Sérum", unit: "g/dL", refRange: "3.5-5.0", category: "chimie" },
  { loinc: "6301-6", name: "INR dans Sang capillaire", shortName: "INR", class: "Coagulation", specimen: "Sang capillaire (citrate)", unit: "ratio", refRange: "< 1.1 (sans anticoag)", category: "coagulation" },
  { loinc: "34714-6", name: "INR dans Sang veineux", shortName: "INR (veineux)", class: "Coagulation", specimen: "Sang veineux (citrate)", unit: "ratio", refRange: "2-3 (AVK)", category: "coagulation" },
  { loinc: "32143-0", name: "D-dimères [Masse/Volume] dans Plasma", shortName: "D-dimères", class: "Coagulation", specimen: "Plasma citraté", unit: "µg/mL FEU", refRange: "< 0.5", category: "coagulation" },
];

const CATEGORY_TONE: Record<LoincTest["category"], "default" | "secondary" | "destructive" | "outline"> = {
  chimie: "default",
  hematologie: "secondary",
  microbiologie: "outline",
  immunologie: "outline",
  genetique: "destructive",
  coagulation: "outline",
};

export const Route = createFileRoute("/referential/loinc")({
  component: LOINCBrowser,
});

function LOINCBrowser() {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const prov = useTerminologyRelease("LOINC");

  const filtered = useMemo(() => LOINC_TESTS.filter((t) => {
    if (filterCat !== "all" && t.category !== filterCat) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return t.loinc.includes(s) || t.name.toLowerCase().includes(s) || t.shortName.toLowerCase().includes(s);
  }), [search, filterCat]);

  const categories = Array.from(new Set(LOINC_TESTS.map((t) => t.category)));

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <TestTube className="h-5 w-5" /> LOINC
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Logical Observation Identifiers Names and Codes · {LOINC_TESTS.length} tests référencés · +95 000 dans LOINC complet
        </p>
      </div>

      {{/*
        v10 Sprint 1 — provenance banner.
        Renders in ALL THREE states. A terminology outage must be visible;
        it must never look like a clean, certified page.
      */}}
      <div
        data-testid="terminology-provenance"
        data-status={prov.status}
        className={
          prov.status === "unavailable" || prov.status === "ok"
            ? "rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
            : "sr-only"
        }
      >
        {prov.status === "loading" && <span>Chargement de la source LOINC…</span>}
        {prov.status === "unavailable" && (
          <><strong>LOINC :</strong> {prov.message}</>
        )}
        {prov.status === "ok" && prov.release?.isAuthoritative && (
          <><strong>LOINC :</strong> version certifiée {prov.release.version} ({prov.release.publisher ?? "source non précisée"})</>
        )}
        {prov.status === "ok" && !prov.release?.isAuthoritative && (
          <><strong>LOINC :</strong> {prov.warning}</>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (code, nom court, nom complet)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <Button size="sm" variant={filterCat === "all" ? "default" : "outline"} onClick={() => setFilterCat("all")}>
          Tous
        </Button>
        {categories.map((c) => (
          <Button key={c} size="sm" variant={filterCat === c ? "default" : "outline"} onClick={() => setFilterCat(c)}>
            {c} ({LOINC_TESTS.filter((t) => t.category === c).length})
          </Button>
        ))}
      </div>

      <div className="space-y-1.5">
        {filtered.map((t) => (
          <Card key={t.loinc}>
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Beaker className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <code className="font-mono text-xs font-bold text-primary">{t.loinc}</code>
                    <span className="font-medium text-sm">{t.shortName}</span>
                    <Badge variant={CATEGORY_TONE[t.category]} className="text-[10px]">{t.category}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{t.name}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {t.specimen} · Unité {t.unit} · Ref {t.refRange}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
